import type { FormEvent, ReactNode } from "react";

import { Box, Divider, Group, Text } from "metabase/ui";

import { renderDefaultSubmitButton } from "../../utils";
import { TimeToggle } from "../TimeToggle";
import { clearTimePart } from "../utils";

import { DateRangePickerBody } from "./DateRangePickerBody";
import type { DateRangePickerValue } from "./types";

export type DateRangePickerProps = {
  value: DateRangePickerValue;
  hasTimeToggle: boolean;
  renderSubmitButton?: () => ReactNode;
  onChange: (value: DateRangePickerValue) => void;
  onSubmit: () => void;
  timezone?: string;
  timezoneLabel?: string;
};

export function DateRangePicker({
  value,
  hasTimeToggle,
  renderSubmitButton = renderDefaultSubmitButton,
  onChange,
  onSubmit,
  timezone,
  timezoneLabel,
}: DateRangePickerProps) {
  const { dateRange, hasTime } = value;
  const [startDate, endDate] = dateRange;
  const displayTimezone = timezoneLabel ?? timezone;
  const hasFooterInfo = hasTimeToggle || displayTimezone;

  const handleDateRangeChange = (newDateRange: [Date, Date]) => {
    onChange({ dateRange: newDateRange, hasTime });
  };

  const handleTimeToggle = () => {
    onChange({
      dateRange: [clearTimePart(startDate), clearTimePart(endDate)],
      hasTime: !hasTime,
    });
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box p="md">
        <DateRangePickerBody
          value={dateRange}
          hasTime={hasTime}
          onChange={handleDateRangeChange}
        />
      </Box>
      <Divider />
      <Group
        p="sm"
        justify={hasFooterInfo ? "space-between" : "flex-end"}
        wrap="wrap"
      >
        {hasFooterInfo && (
          <Group gap="sm" wrap="wrap">
            {hasTimeToggle && (
              <TimeToggle hasTime={hasTime} onClick={handleTimeToggle} />
            )}
            {displayTimezone && (
              <Text
                c="text-medium"
                size="sm"
                style={{ overflowWrap: "anywhere" }}
              >
                {displayTimezone}
              </Text>
            )}
          </Group>
        )}
        {renderSubmitButton()}
      </Group>
    </form>
  );
}
